import React, {useMemo} from 'react';
import styled from 'styled-components';
import {ButtonDropdownField, DropdownList, FlexRow, Icon} from '@forecast-it/design-system';
import {FormattedMessage, useIntl} from 'react-intl';
import {useReportBuilder} from './ReportBuilderContext';
import {orderedDataColumnIds} from './ReportTable/useReportTableColumns';
import {DEFAULT_FIELD_LABELS} from './ReportTable/fieldLabels';
import {fieldByKey} from './contracts/report-taxonomy.types';

export interface ColumnManagerProps {
	fieldLabels?: Record<string, string>;
}

/** Taxonomy columns worth offering when a report does not already carry them. The dropdown is a
 * visibility toggle over the definition's own columns, so a column absent from `fields` has no way
 * into a report otherwise — the AI has to be asked for it. Deliverable is the one users ask for by
 * hand, so it is offered; the list is deliberately short rather than the whole taxonomy, because
 * every offered column that gets ticked is another path added to the fetch. */
const OFFERABLE_COLUMN_IDS = ['task.deliverable.name'];

/** Merges the definition's canonical leaf-column order with the current `tableState.columnOrder`:
 * current order wins for columns already present (so user reordering persists across renders),
 * any newly-added field path (not yet in `columnOrder`, e.g. after an AI patch) is appended at the
 * end in canonical order. Fixed (`__`-prefixed) columns are excluded — they are never toggleable
 * and cannot be reordered. */
function resolveColumnIds(definitionFieldPaths: string[], currentOrder: string[]): string[] {
	const currentDataIds = currentOrder.filter(id => !id.startsWith('__'));
	const currentSet = new Set(currentDataIds);
	const missing = definitionFieldPaths.filter(id => !currentSet.has(id));
	return [...currentDataIds, ...missing];
}

/** DS DropdownList renders its root as a flex:1 item, which grows to fill the controls row and
 * left-aligns the trigger (defeating the row's justify-content:flex-end). Wrap it in a hugging
 * inline-flex container (flex:0 0 auto) so the Columns button stays content-width and the Group By
 * + Columns cluster sits at the right, as in the design. (GroupByControl gets this from its FlexColumn.) */
const ColumnsTriggerWrapper = styled.div`
	display: inline-flex;
	flex: 0 0 auto;
`;

/** Columns dropdown: one checkbox row per leaf data column (visibility toggle). Fixed
 * structural columns (expander/favourite) are never listed. Column reordering is available via
 * dragging table headers. Operates through `useReportBuilder()`'s `tableState`/`updateTableState`,
 * plus `addColumn` for the offered columns below.
 *
 * Two kinds of row are listed. The report's own columns behave as they always have: checked when
 * visible, and ticking toggles visibility. An OFFERABLE column the report does not carry is listed
 * unchecked, and ticking it adds it to the definition — after which it is an ordinary column of the
 * first kind. Offers are gated on the taxonomy actually publishing the field, so a report-service
 * that has not shipped one shows no dead option. */
export function ColumnManager({fieldLabels}: ColumnManagerProps): JSX.Element {
	const {formatMessage} = useIntl();
	const {definition, tableState, updateTableState, taxonomy, addColumn} = useReportBuilder();

	const labels = useMemo(() => ({...DEFAULT_FIELD_LABELS, ...fieldLabels}), [fieldLabels]);

	const columnIds = useMemo(
		() => resolveColumnIds(orderedDataColumnIds(definition), tableState.columnOrder),
		[definition, tableState.columnOrder]
	);

	const offeredIds = useMemo(() => {
		if (!taxonomy) {
			return [];
		}
		const present = new Set(columnIds);
		return OFFERABLE_COLUMN_IDS.filter(id => !present.has(id) && fieldByKey(taxonomy, id) !== undefined);
	}, [taxonomy, columnIds]);

	const toggleVisibility = (columnId: string): void => {
		const isVisible = tableState.columnVisibility[columnId] !== false;
		updateTableState({
			columnVisibility: {...tableState.columnVisibility, [columnId]: !isVisible},
		});
	};

	return (
		<ColumnsTriggerWrapper>
			<DropdownList>
				<DropdownList.Trigger>
					{/* the design: Columns is the neutral field-style control (grey border #6b6d7b,
				    dark #232334 text, leading list glyph + built-in trailing chevron) — the same
				    "Filter" component as Download, NOT a brand-blue ButtonDropdown. aria-label carries
				    the accessible name since the label sits inside a FlexRow rather than as text. */}
					<ButtonDropdownField
						width={'fit-content'}
						aria-label={formatMessage({id: 'ai_report_builder.columns'})}
						data-cy={'ai-report-column-manager'}
					>
						<FlexRow alignItems={'center'} gap={'xs'}>
							<Icon icon={'list'} newColor={'onlight'} size={'s'} />
							<FormattedMessage id={'ai_report_builder.columns'} />
						</FlexRow>
					</ButtonDropdownField>
				</DropdownList.Trigger>
				<DropdownList.Content offset={4}>
					{columnIds.map(columnId => (
						<DropdownList.CheckboxItem
							key={columnId}
							checked={tableState.columnVisibility[columnId] !== false}
							onChange={() => toggleVisibility(columnId)}
							data-cy={`ai-report-column-toggle-${columnId}`}
						>
							{labels[columnId] ?? columnId}
						</DropdownList.CheckboxItem>
					))}
					{offeredIds.map(columnId => (
						<DropdownList.CheckboxItem
							key={columnId}
							checked={false}
							onChange={() => addColumn(columnId)}
							data-cy={`ai-report-column-toggle-${columnId}`}
						>
							{labels[columnId] ?? columnId}
						</DropdownList.CheckboxItem>
					))}
				</DropdownList.Content>
			</DropdownList>
		</ColumnsTriggerWrapper>
	);
}
